import { type CSSProperties, type MutableRefObject, memo, type ReactNode, useCallback, useEffect, useRef } from 'react';
import isEqual from 'react-fast-compare';
import { List, type OverscanIndices, type OverscanIndicesGetterParams, WindowScroller } from 'react-virtualized';

import { GalleryViewBox } from './GalleryViewBox';
import { type GalleryItem, type GallerySection } from './types';
import { calculateListRowHeight } from './utils/calculateListRowHeight';
import { sectionsToRows } from './utils/sectionsToRows';

const itemRenderer = ({ box, render, index }: GalleryItem, isVisible: boolean) => (
  <GalleryViewBox box={box} index={index} isVisible={isVisible} key={index} render={render} />
);

/*
 * react-virtualized's default (`accessibilityOverscanIndicesGetter`) spends the whole overscan
 * budget in the direction of travel: scrolling forward it renders `overscanRowCount` rows BELOW the
 * viewport and exactly ONE above it. That one row of headroom means any small over-report of
 * `scrollTop` unmounts a row that is still on screen — the row blanks out as it passes under the
 * top of the scroll container. Overscanning symmetrically keeps the same rows mounted in both
 * directions, so a transient measurement skew costs nothing visible.
 */
const symmetricOverscanIndicesGetter = ({
  cellCount,
  overscanCellsCount,
  startIndex,
  stopIndex,
}: OverscanIndicesGetterParams): OverscanIndices => ({
  overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
  overscanStopIndex: Math.min(cellCount - 1, stopIndex + overscanCellsCount),
});

export type GalleryViewProps = {
  sections: GallerySection[];
  width: number;
  rowRenderer?: (params: {
    index: number;
    style: CSSProperties;
    row: GalleryItem[];
    isVisible: boolean;
    itemRenderer: (item: GalleryItem, isVisible: boolean) => ReactNode;
  }) => ReactNode;
  style?: CSSProperties;
  scrollElementRef: MutableRefObject<HTMLDivElement | HTMLElement | null>;
};

/**
 * This component displays Gallery view
 */
export const GalleryView = memo(({ scrollElementRef, sections, width, style, rowRenderer }: GalleryViewProps) => {
  const rows = sectionsToRows(sections);

  const listRef = useRef<List | null>(null);
  const windowScrollerRef = useRef<WindowScroller | null>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <We want to recompute the row heights when the rows change>
  useEffect(() => {
    listRef.current?.recomputeRowHeights();
    // Row changes can also move the grid within the scroll container (a section collapsing, a page
    // of results landing above it). WindowScroller only re-measures its own offset on mount, on a
    // scrollElement swap, or when the scrollElement itself resizes — none of which fire here.
    windowScrollerRef.current?.updatePosition();
  }, [rows]);

  /*
   * WindowScroller subtracts a MOUNT-TIME snapshot of the grid's offset within the scroll container
   * (`_positionFromTop`) from the container's scrollTop. Anything ABOVE the grid that grows after
   * that snapshot is taken — a showcase's image blocks resolving their intrinsic height, a video
   * block swapping its placeholder box for real metadata, an expanding section — pushes the grid
   * down without WindowScroller noticing, so it over-reports scrollTop and unmounts rows that are
   * still on screen. Nothing in react-virtualized watches for this: its own resize listener is on
   * the scroll container, whose box does not change when its CONTENT grows.
   *
   * So watch the container's content instead and re-measure whenever it changes size.
   * `updateScrollTopOnUpdatePosition` makes WindowScroller recompute scrollTop from the corrected
   * offset immediately — without it the grid stays wrong until the visitor scrolls again.
   */
  const scrollElement = scrollElementRef.current;

  useEffect(() => {
    if (!scrollElement || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      windowScrollerRef.current?.updatePosition();
    });

    // The children are the content that grows; the container's own box usually does not.
    for (const child of Array.from(scrollElement.children)) {
      observer.observe(child);
    }

    return () => observer.disconnect();
  }, [scrollElement]);

  const getRowHeight = useCallback(({ index }: { index: number }) => calculateListRowHeight({ index, rows }), [rows]);

  return scrollElement ? (
    <WindowScroller ref={windowScrollerRef} scrollElement={scrollElement} updateScrollTopOnUpdatePosition>
      {({ height, scrollTop, isScrolling }) => (
        <List
          autoHeight
          // The visible height of the SCROLL CONTAINER, which is what bounds the rendered window —
          // `window.innerHeight` is only the same number when the container happens to be the
          // full viewport, and is plain wrong wherever the gallery scrolls inside a shorter box
          // (the showcase editor's inline canvas). WindowScroller keeps this in sync on resize;
          // the viewport is the last-resort fallback for a container it could not measure yet.
          height={height || (typeof window !== 'undefined' ? window.innerHeight : 0)}
          isScrolling={isScrolling}
          overscanIndicesGetter={symmetricOverscanIndicesGetter}
          overscanRowCount={2}
          ref={listRef}
          rowCount={rows.length}
          rowHeight={getRowHeight}
          rowRenderer={({ index, style, isVisible }) => {
            const row = rows[index];

            if (!row) {
              return <div key={index} style={{ background: 'white', height: 100, width: 100 }} />;
            }

            return rowRenderer ? (
              rowRenderer({ index, isVisible, itemRenderer, row, style })
            ) : (
              <div key={index} style={style}>
                {row.map((item) => itemRenderer(item, isVisible))}
              </div>
            );
          }}
          scrollTop={scrollTop}
          style={style}
          tabIndex={-1}
          width={width}
        />
      )}
    </WindowScroller>
  ) : null;
}, isEqual);

GalleryView.displayName = 'GalleryView';
